import { useEffect, useRef, useState } from "react";
import { toFixed } from "@shared/utils/numbers";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants";

interface UseLiveAvgPriceChangeProps {
  symbol: string;
  initialChange: number;
  initialChangeTimestamp?: number;
}

const useLiveAvgPriceChange = ({
  symbol,
  initialChange,
  initialChangeTimestamp,
}: UseLiveAvgPriceChangeProps) => {
  const changeArrayRef = useRef<number[]>(undefined);
  const [changeValue, setChangeValue] = useState<number | undefined>(undefined);
  const socketChangeTimestampRef = useRef<number>(undefined);
  const value = toFixed(
    ((changeValue ?? initialChange) || 0) * 100,
    PERCENT_PRECISION
  );
  const shouldResetSocketPrice =
    initialChangeTimestamp &&
    socketChangeTimestampRef.current &&
    socketChangeTimestampRef.current < initialChangeTimestamp;

  const resetSocketData = () => {
    changeArrayRef.current = [];
    setChangeValue(undefined);
  };

  useEffect(() => {
    if (shouldResetSocketPrice) {
      resetSocketData();
    }
  }, [shouldResetSocketPrice]);

  useEffect(() => {
    resetSocketData();
  }, [symbol]);

  useEffect(() => {
    const interval = setInterval(() => {
      const filteredArray = changeArrayRef.current?.filter(
        (change) => change !== undefined
      );
      const numExchanges = filteredArray?.length;
      const changeSum = (filteredArray as number[]).reduce(
        (sum, change) => sum + change,
        0
      );
      setChangeValue(
        numExchanges && changeSum ? changeSum / numExchanges : initialChange
      );
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const setChange = (change: number, index: number) => {
    if (changeArrayRef.current) {
      changeArrayRef.current[index] = change;
      socketChangeTimestampRef.current = Date.now();
    }
  };

  return { value, setChange };
};

export default useLiveAvgPriceChange;
